import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Button, Checkbox, Drawer, Flex, Text } from '@mantine/core';
import { FormProvider } from 'react-hook-form';
import { Select } from 'react-hook-form-mantine';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { GetPersonInvestmentProgrammesRes } from '@dealstack/shared/lib/generated';
import { INVESTMENT_PROGRAMME_FORM_FIELDS } from './utils/types.ts';
import { constructTransactionSentence } from '@dealstack/frontend/shared/utils/constructTransactionSentence.ts';
import { useUnlinkInvestmentProgrammeDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/InvestmentProgrammeDrawer/hooks/useUnlinkInvestmentProgrammeDrawer.ts';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	linkedMip: GetPersonInvestmentProgrammesRes | null;
	opened: boolean;
	onClose: () => void;
}

export const UnlinkPersonInvestmentProgrammeDrawer = ({ linkedMip, opened, onClose }: Props) => {
	const tCapManage = useTranslationCapManage();

	const {
		unLinkMipForm,
		onFormSubmit,
		isLoading,
		unlinkTransactions,
		setUnlinkTransactions,
		personTransactionsForMip,
		personMipOpenTsaWarning,
	} = useUnlinkInvestmentProgrammeDrawer(opened, onClose, linkedMip);

	const availableMipOptions = linkedMip
		? [
				{
					label: linkedMip.programmeName ?? '',
					value: linkedMip.id,
				},
			]
		: [];

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tCapManage('person_investment_programme.unlink_drawer.title')}
		>
			<FormProvider {...unLinkMipForm}>
				<Flex direction="column" justify="space-between" h="100%">
					<Flex direction="column" gap="1rem">
						<Select
							name={INVESTMENT_PROGRAMME_FORM_FIELDS.mip_id}
							control={unLinkMipForm.control}
							label={tCapManage('person_investment_programme.drawer.form.mip_label')}
							placeholder={tCapManage(
								'person_investment_programme.drawer.form.mip_placeholder'
							)}
							data={availableMipOptions}
							rightSection={<Icon variant="caret-down" size="lg" />}
							styles={{ rightSection: { pointerEvents: 'none' } }}
							searchable
							size="md"
							disabled={true}
						/>

						{personMipOpenTsaWarning?.hasOpenTsa && (
							<InfoBox
								iconVariant="warning"
								rippleColor="orange"
								description={tCapManage(
									'person_investment_programme.unlink_drawer.open_tsa_warning'
								)}
								dataTestId={
									TEST_ELEMENT_IDS.CAP_INVESTMENT_PROGRAMMES_UNLINK_DRAWER_TSA_WARNING
								}
							/>
						)}

						{!!personTransactionsForMip?.length && (
							<Flex direction="column" gap="0.25rem">
								<Checkbox
									checked={unlinkTransactions}
									onChange={() => setUnlinkTransactions(!unlinkTransactions)}
									label={tCapManage(
										'person_investment_programme.drawer.form.unlink_transactions_label',
										{
											count: personTransactionsForMip.length,
										}
									)}
									data-testid={
										TEST_ELEMENT_IDS.CAP_INVESTMENT_PROGRAMMES_UNLINK_DRAWER_TRANSACTIONS_CHECKBOX
									}
								/>
								{personTransactionsForMip.map((t) => (
									<Text key={t.id}>
										{constructTransactionSentence({
											transactionType: t.type,
											units: t.units ?? 0,
											securityUnitDenominator: t.securityUnitDenominator,
											transactionDate: t.date,
											buyerName: t.destinationPersonName,
											sellerName: t.sourcePersonName,
											securityName: t.securityName ?? '',
											cost: t.cost ?? 0,
										})}
									</Text>
								))}
							</Flex>
						)}
					</Flex>

					<Button
						variant="filled"
						bg="red.6"
						onClick={unLinkMipForm.handleSubmit(onFormSubmit)}
						disabled={isLoading}
						data-testid={
							TEST_ELEMENT_IDS.CAP_INVESTMENT_PROGRAMMES_UNLINK_DRAWER_SUBMIT_BUTTON
						}
					>
						{tCapManage('person_investment_programme.unlink_drawer.unlink_button')}
					</Button>
				</Flex>
			</FormProvider>
		</Drawer>
	);
};
